import { ApolloError } from '@apollo/client';
import { apolloClient } from 'graphql/apolloClient';
import { UPDATE_OBJECT } from 'graphql/UserInstanceAPI/mutations';
import { CompleteObjectInstance } from 'types/interfaces';
import { BugTracker } from 'Utils/Bugtracker';
import { removeTypenames } from '../User Instance API/mutations';
import { GetCompleteUserInstanceDetail } from '../User Instance API';
import { queryClient } from 'queryClient';
import { cosneticsQueryKeys } from '../cosneticsQueryKeys';
import { normalizeCompleteObjectInstance } from '../User Instance API/normalizers';

/**
 * Updates an CompleteObjectInstanceList using the provided parameters.
 * @param {string} baseUrl - The base URL for the hostname.
 * @param {number} ProcessInstanceId - The identifier of the ProcessInstanceId to update.
 * @param {string} action - The action to perform.
 * @param {CompleteObjectInstance} data - The data object containing the CompleteObjectInstanceList information.
 * @returns {Promise<CompleteObjectInstance | ApolloError>} - A promise resolving to the updated CompleteObjectInstanceList or an ApolloError.
 */
export const UpdateObject = async ({
  baseUrl,
  ProcessInstanceId,
  action,
  data: payload,
  fetchPolicy
}: {
  baseUrl: string;
  ProcessInstanceId: number;
  action: string;
  data: CompleteObjectInstance;
  fetchPolicy?: 'cache-first' | 'network-only' | 'no-cache';
}): Promise<CompleteObjectInstance | ApolloError> => {
  try {
    const finalPayload = normalizeCompleteObjectInstance(
      removeTypenames(payload)
    ) as CompleteObjectInstance;

    const { data, errors } = await apolloClient.mutate({
      mutation: UPDATE_OBJECT,
      variables: {
        baseUrl,
        processInstanceId: ProcessInstanceId,
        action,
        data: finalPayload
      }
    });

    if (errors && errors.length > 0) {
      return new ApolloError({
        errorMessage: errors.map((err) => err.message).join(', ')
      });
    }

    const userInstanceId = Number(finalPayload.ObjectInstance.UserInstanceId);
    if (userInstanceId) {
      await GetCompleteUserInstanceDetail({
        baseUrl,
        fetchPolicy: true,
        UserInstanceId: userInstanceId
      });
    }
    await queryClient.invalidateQueries(
      cosneticsQueryKeys.usersForProcessAll
    );

    return data.UpdateObject as CompleteObjectInstance;
  } catch (e) {
    BugTracker.notify(e);

    const error = e as Error;
    const errorMessage = error.message || 'An unexpected error occurred';
    return new ApolloError({ errorMessage });
  }
};
